import { isFulfilled } from "@reduxjs/toolkit";
import { format, isValid, parseISO } from "date-fns";
import {
  FC,
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import DatePicker from "react-datepicker";
import {
  DEFAULT_ITEMS_PER_PAGE,
  DEFAULT_SEARCH_INPUT_DEBOUNCE_DELAY,
  DEFAULT_SEARCH_INPUT_LENGTH,
} from "../../../constants/app";
import useDebouncedValue from "../../../hooks/useDebouncedValue";
import { usePrevious } from "../../../hooks/usePrevious";
import Roles from "../../../models/roles";
import {
  useAppDispatch as useDispatch,
  useAppSelector as useSelector,
} from "../../../store";
import {
  hideTransparentPreloaderAction,
  showNotificationAction,
  showTransparentPreloaderAction,
} from "../../../store/application/slice";
import {
  getDateParamsForAPI,
  getDefaultDateRange,
  isSameDateRange,
} from "../../../store/timesheet/parser";
import {
  exportTimesheetsThunk,
  getAllCandidateTimesheetCentresThunk,
  getCandidatesForExportThunk,
  getTimesheetHistoryThunk
} from "../../../store/timesheet/thunks";
import { ITimesheetData, TTimesheetSort } from "../../../store/timesheet/types";
import { SortOrder } from "../../../store/vacancy/types";
import PaginationControls from "../../Pagination/PaginationControls";
import CustomButton from "../../UI/Buttons/CustomButton";
import ChevronOthers from "../../UI/Icons/ChevronOthers";
import Download from "../../UI/Icons/Download";
import PopupModal from "../../UI/Popup";
import CustomSelect from "../../UI/Select";
import SearchAndFilter from "./common/SearchAndFilter";
import TableHeader from "./common/TableHeader";
import TableRow from "./common/TableRow";
import TabsHeader from "./common/TabsHeader";
import ViewTimesheet from "./common/ViewTimesheet";
import TimeSheetAlert from "./TimeSheetAlert";

type CustomDateInputProps = {
  value?: string;
  onClick?: () => void;
  isOpen?: boolean;
};

const AdminTimesheetHistory: FC = () => {
  const dispatch = useDispatch();

  const user = useSelector((state) => state.auth.user);
  const { historyTimesheets, historyPagination, totalOverDueTimesheetCount, totalPendingTimesheetCount } = useSelector(
    (state) => state.timesheet
  );
  const allCentresList = useSelector((state) => state.timesheet.allCentersList);
  const allCandidatesList = useSelector(
    (state) => state.timesheet.allCandidatesForExportTimesheet
  );

  const centresOptions = [
    { value: "", label: `All Centres (${allCentresList.length})` },
    ...allCentresList.map((b) => ({
      value: b.id,
      label: b?.business_name ?? "",
    })),
  ];

  const candidateOptions = [
    { value: "", label: `All Employees (${allCandidatesList.length})` },
    ...allCandidatesList.map((b) => ({
      value: b.id,
      label: b.full_name,
    })),
  ];

  const isClient =
    (user?.role?.name as Roles) === Roles.CENTER_ADMIN ||
    (user?.role?.name as Roles) === Roles.CENTER_USER;

  const [selectedTimesheet, setSelectedTimesheet] =
    useState<ITimesheetData | null>(null);
  const [showViewTimesheetModal, setShowViewTimesheetModal] =
    useState<boolean>(false);
  const [showExportTimesheetModal, setShowExportTimesheetModal] =
    useState<boolean>(false);
  const [startDate, setStartDate] = useState<Date | null>(() => {
    const today = new Date();
    today.setDate(today.getDate() - 7); // add 7 days
    return today;
  });
  const [endDate, setEndDate] = useState<Date | null>(new Date());
  const [isStartCalendarOpen, setIsStartCalendarOpen] =
    useState<boolean>(false);
  const [isEndCalendarOpen, setIsEndCalendarOpen] = useState<boolean>(false);
  const [selectedCentre, setSelectedCentre] = useState<number[]>([]);
  const [selectedEmployee, setSelectedEmployee] = useState<number[]>([]);
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Local states for sorting
  const [sortBy, setSortBy] = useState<TTimesheetSort>("sent_to_payroll_on");
  const [sortOrder, setSortOrder] = useState<SortOrder>("desc");

  // Search and debouncing states
  const [keyword, setKeyword] = useState("");
  const [selectedCentreIds, setSelectedCentreIds] = useState<number[]>([]);
  const [selectedCandidateIds, setSelectedCandidateIds] = useState<number[]>(
    []
  );
  const [dateRange, setDateRange] = useState<[Date | null, Date | null] | null>(
    getDefaultDateRange(30)
  );

  const [searchInput, setSearchInput] = useState<string>("");
  const debouncedSearch = useDebouncedValue(
    searchInput,
    DEFAULT_SEARCH_INPUT_DEBOUNCE_DELAY
  );

  // Pagination states
  const [page, setPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<number>(DEFAULT_ITEMS_PER_PAGE);
  const currentParams = useMemo(
    () => ({
      keyword,
      selectedCentreIds,
      selectedCandidateIds,
      dateRange,
      page,
      perPage,
      sortBy,
      sortOrder,
    }),
    [
      keyword,
      selectedCentreIds,
      selectedCandidateIds,
      dateRange,
      page,
      perPage,
      sortBy,
      sortOrder,
    ]
  );

  const previousParams = usePrevious(currentParams);

  // useEffect(() => {
  //   dispatch(getAllCandidateTimesheetCentresThunk());
  // }, [dispatch]);

  const CustomDateInput = forwardRef<HTMLDivElement, CustomDateInputProps>(
    ({ value, onClick, isOpen }, ref) => (
      <div
        onClick={onClick}
        ref={ref}
        className="react-datepicker-ignore-onclickoutside bg-white border border-off-white radius-8px py-10px px-15px
                  d-flex gap-3 align-items-center justify-content-between cursor-pointer"
      >
        <span>{value || "Select date"}</span>
        <ChevronOthers isOpen={isOpen ?? false} />
      </div>
    )
  );

  // Handle sorting logic
  const handleSortChange = (field: TTimesheetSort) => {
    if (sortBy === field) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(field);
      setSortOrder("asc");
    }
  };

  // Handle pagination link click
  const handlePageChange = (url: string | null) => {
    if (url) {
      const page = new URL(url).searchParams.get("page");
      setPage(Number(page));
    }
  };

  const handleViewTimesheet = (timesheet: ITimesheetData) => {
    setSelectedTimesheet(timesheet);
    setShowViewTimesheetModal(true);
  };

  const onClickClose = () => {
    setSelectedTimesheet(null);
    setShowViewTimesheetModal(false);
    setShowExportTimesheetModal(false);
  };

  const fetchTimesheetsHistory = useCallback(async () => {
    const prev = previousParams;

    const areSame =
      prev &&
      prev.keyword === currentParams.keyword &&
      prev.page === currentParams.page &&
      prev.perPage === currentParams.perPage &&
      prev.sortBy === currentParams.sortBy &&
      prev.sortOrder === currentParams.sortOrder &&
      prev.selectedCentreIds === currentParams.selectedCentreIds &&
      prev.selectedCandidateIds === currentParams.selectedCandidateIds &&
      isSameDateRange(prev?.dateRange, dateRange);

    if (areSame) {
      return; // Skip if no actual change
    }

    setIsLoading(true);
    dispatch(showTransparentPreloaderAction());
    const { start_date, end_date } = getDateParamsForAPI(dateRange);

    try {
      // Now, make the timesheet request
      await dispatch(
        getTimesheetHistoryThunk({
          searchValue: currentParams.keyword,
          page: currentParams.page,
          per_page: currentParams.perPage,
          sort_by: currentParams.sortBy,
          sort_order: currentParams.sortOrder,
          start_date,
          end_date,
          centres: currentParams.selectedCentreIds,
          candidates: currentParams.selectedCandidateIds,
        })
      );
    } finally {
      dispatch(hideTransparentPreloaderAction());
      setIsLoading(false);
    }
  }, [dispatch, currentParams, previousParams, dateRange]);

  useEffect(() => {
    fetchTimesheetsHistory();
  }, [
    fetchTimesheetsHistory,
    keyword,
    dateRange,
    selectedCandidateIds,
    selectedCentreIds,
    page,
    perPage,
    sortBy,
    sortOrder,
  ]);

  // Update debounced keyword and reset page when typing stops
  useEffect(() => {
    if (
      debouncedSearch.length === 0 ||
      debouncedSearch.length > DEFAULT_SEARCH_INPUT_LENGTH
    ) {
      setKeyword(debouncedSearch);

      setPage(1); // reset to first page on new search
    }
  }, [debouncedSearch]);

  const handleCentreSelect = (ids: number[]) => {
    setSelectedCentreIds(ids);
  };

  useEffect(() => {
    const fetchExportData = async () => {
      const start_date = startDate ? format(startDate, "yyyy-MM-dd") : "";
      const end_date = endDate ? format(endDate, "yyyy-MM-dd") : "";
      
      dispatch(showTransparentPreloaderAction());
      try {
        await Promise.all([
          dispatch(
            getCandidatesForExportThunk({ type: "history", start_date, end_date })
          ),
          dispatch(
            getAllCandidateTimesheetCentresThunk({
              type: "history",
              start_date,
              end_date,
            })
          )
        ]);
      } finally {
        dispatch(hideTransparentPreloaderAction());
      }
    };
    
    fetchExportData();
  }, [startDate, endDate, dispatch]);

  const handleOpenExportTimesheet = async () => {
    const start_date = startDate ? format(startDate, "yyyy-MM-dd") : "";
    const end_date = endDate ? format(endDate, "yyyy-MM-dd") : "";
     setShowExportTimesheetModal(true);
    // dispatch(showTransparentPreloaderAction());
    // const res = await dispatch(
    //   getCandidatesForExportThunk({ type: "history", start_date, end_date })
    // );
    // const res1 = await dispatch(
    //   getAllCandidateTimesheetCentresThunk({
    //     type: "history",
    //     start_date,
    //     end_date,
    //   })
    // );
    
    // if (isFulfilled(res) && isFulfilled(res1)) {
    //   dispatch(hideTransparentPreloaderAction());
    //   setShowExportTimesheetModal(true);
    // }
    // dispatch(hideTransparentPreloaderAction());
  };

  const handleFieldChange = (type: string, value: any) => {
    if (type === "centres") {
      setSelectedCentre(value ? [value] : []);
    } else if (type === "employees") {
      setSelectedEmployee(value ? [value] : []);
    }
  };

  const handleExportTimesheet = async () => {
    setIsExporting(true);
    dispatch(showTransparentPreloaderAction());
    
    // Allow UI to update before heavy operation
    await new Promise(resolve => setTimeout(resolve, 50));
    
    // Validate dates
    if (!startDate || !endDate) {
      console.warn("Invalid date range: startDate or endDate is missing");
      dispatch(hideTransparentPreloaderAction());
      setIsExporting(false);
      return;
    }

    const type = "history";
    const exportFormat: "excel" | "pdf" = "pdf";

    // Format the dates
    const start_date = format(startDate, "yyyy-MM-dd");
    const end_date = format(endDate, "yyyy-MM-dd");

    try {
      const result = await dispatch(
        exportTimesheetsThunk({
          start_date,
          end_date,
          type,
          export: exportFormat,
          centre_ids: selectedCentre,
          candidate_ids: selectedEmployee,
          export_type: "manual"
        })
      );

      if (!isFulfilled(result)) {
        console.error("Export request failed", result);
        dispatch(hideTransparentPreloaderAction());
        setIsExporting(false);
        return;
      }

      // Success - close modal and reset state
      setShowExportTimesheetModal(false);
      setSelectedCentre([]);
      setSelectedEmployee([]);
      setIsExporting(false);
      dispatch(showNotificationAction("Exported!"));

      const blobData = result.payload;
      const fileExtension = exportFormat === "pdf" ? "pdf" : "xlsx";
      const mimeType =
        exportFormat === "pdf"
          ? "application/pdf"
          : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

      const blob = new Blob([blobData], { type: mimeType });
      const url = URL.createObjectURL(blob);

      const filename = `timesheets_${type}_${start_date}_to_${end_date}.${fileExtension}`;
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Unexpected error during export:", error);
    } finally {
      dispatch(hideTransparentPreloaderAction());
      setIsExporting(false);
    }
  };

  const handleExportTimesheetGeneric = async (
    exportFormat: "excel" | "pdf"
  ) => {
    dispatch(showTransparentPreloaderAction());
    if (!dateRange || dateRange.length < 2 || !dateRange[0] || !dateRange[1]) {
      console.warn("Invalid date range");
      dispatch(hideTransparentPreloaderAction());
      return;
    }

    const type = "history";
    const start =
      typeof dateRange[0] === "string" ? parseISO(dateRange[0]) : dateRange[0];
    const end =
      typeof dateRange[1] === "string" ? parseISO(dateRange[1]) : dateRange[1];

    if (!isValid(start) || !isValid(end)) {
      console.warn("Invalid date objects");
      dispatch(hideTransparentPreloaderAction());
      return;
    }

    const start_date = format(start, "yyyy-MM-dd");
    const end_date = format(end, "yyyy-MM-dd");

    try {
      const result = await dispatch(
        exportTimesheetsThunk({
          start_date,
          end_date,
          type,
          export: exportFormat,
          centre_ids: currentParams.selectedCentreIds,
          candidate_ids: currentParams.selectedCandidateIds,
          export_type: "manual"
        })
      );

      if (!isFulfilled(result)) {
        console.error("Export request failed", result);
        dispatch(hideTransparentPreloaderAction());
        return;
      }

      dispatch(hideTransparentPreloaderAction());
      const blobData = result.payload;
      const fileExtension = exportFormat === "pdf" ? "pdf" : "xlsx";
      const mimeType =
        exportFormat === "pdf"
          ? "application/pdf"
          : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

      const blob = new Blob([blobData], { type: mimeType });
      const url = URL.createObjectURL(blob);

      const filename = `timesheets_${type}_${start_date}_to_${end_date}.${fileExtension}`;
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);      
    } catch (error) {
      console.error("Unexpected error during export:", error);
    }
    dispatch(hideTransparentPreloaderAction());
  };


  return (
    <>
      <PopupModal
        visible={showExportTimesheetModal}
        showCloseBtn={true}
        onClickClose={onClickClose}
        customWidth={650}
        popUpClass="mobile-model"
      >
        <div className="d-flex flex-column gap-15">
          <div className="fs-22 fw-400 text-black">Export PDF Report</div>
          <div className="bg-off-white h-1px"></div>
          <div className="d-flex flex-column gap-15 mobile-job-scroll">
            <div className="fs-16">Only days worked will display in the report. Any unpaid leave will not show.</div>
            <div className="fw-700 fs-16 text-black">
              Customise your report settings:
            </div>
            <div className="d-flex flex-column flex-lg-row gap-15 align-items-lg-center">
              <div className="fw-400 fs-16 text-black">Date range from</div>
              <div className="position-relative min-w-180px mobile-w-100">
                <DatePicker
                  selected={startDate}
                  onChange={(date: Date | null) => setStartDate(date)}
                  dateFormat="EEE d MMM yyyy"
                  maxDate={new Date()}
                  placeholderText="Start date"
                  onCalendarOpen={() => setIsStartCalendarOpen(true)}
                  onCalendarClose={() => setIsStartCalendarOpen(false)}
                  customInput={<CustomDateInput isOpen={isStartCalendarOpen} />}
                  className="d-none"
                />
              </div>
              <div className="fw-400 fs-16 text-black">to</div>
              <div className="position-relative min-w-180px mobile-w-100">
                <DatePicker
                  selected={endDate}
                  onChange={(date: Date | null) => setEndDate(date)}
                  dateFormat="EEE d MMM yyyy"
                  minDate={startDate ?? undefined}
                  maxDate={new Date()}
                  placeholderText="End date"
                  onCalendarOpen={() => setIsEndCalendarOpen(true)}
                  onCalendarClose={() => setIsEndCalendarOpen(false)}
                  customInput={<CustomDateInput isOpen={isEndCalendarOpen} />}
                  className="d-none"
                  disabled={!startDate}
                />
              </div>
            </div>
            <div className="d-flex flex-column flex-lg-row gap-15 align-items-lg-center">
              <div className="fw-400 fs-16 text-black">Centres</div>
              <div className="min-w-330px mobile-w-100">
                <CustomSelect
                  options={centresOptions}
                  placeholder={`All Centres (${allCentresList.length})`}
                  onSelect={(opt) => handleFieldChange("centres", opt.value)}
                  enableFilter={true}
                  defaultSelected={
                    selectedCentre.length > 0
                      ? {
                          value: selectedCentre[0],
                          label:
                            allCentresList.find(
                              (item) => item.id === selectedCentre?.[0]
                            )?.business_name ?? "",
                        }
                      : centresOptions[0] // fallback to "All Centres"
                  }
                />
              </div>
            </div>
            <div className="d-flex flex-column flex-lg-row gap-15 align-items-lg-center">
              <div className="fw-400 fs-16 text-black">Employee</div>
              <div className="min-w-330px mobile-w-100">
                <CustomSelect
                  options={candidateOptions}
                  placeholder={`All Employees (${allCandidatesList.length})`}
                  onSelect={(opt) => handleFieldChange("employees", opt.value)}
                  enableFilter={true}
                  defaultSelected={
                    selectedEmployee.length > 0
                      ? {
                          value: selectedEmployee[0],
                          label:
                            allCandidatesList.find(
                              (item) => item.id === selectedEmployee?.[0]
                            )?.full_name ?? "",
                        }
                      : candidateOptions[0] // fallback to "All Employees"
                  }
                />
              </div>
            </div>
            <div className="d-flex justify-content-end">
              <CustomButton
                variant={"primary"}
                type="button"
                className="btn-theme bg-teal text-black"
                onClick={handleExportTimesheet}
                disabled={isExporting}
              >
                <Download /> {isExporting ? "Exporting..." : "Export"}
              </CustomButton>
            </div>
          </div>
        </div>
      </PopupModal>

      <TimeSheetAlert
        totalPending={totalPendingTimesheetCount || 0}
        totalOverdue={totalOverDueTimesheetCount}
        goToLink="/admin/timesheet/payroll-pending"
      />
      <div className="d-flex flex-column gap-20">
        <div className="d-flex flex-column gap-10">
          <div className="d-flex flex-column">
            <div className="d-flex gap-10 justify-content-between overflow-auto mobile-scroll-hide">
              <TabsHeader
                role={isClient ? "centre_admin" : "super_admin"}
                activeTab="history"
              />
              <div className="d-none d-lg-flex gap-10">
                <CustomButton
                  variant={"primary"}
                  type="button"
                  className="btn-theme bg-white text-black text-nowrap max-h-36px px-0"
                  onClick={handleOpenExportTimesheet}
                >
                  <Download />
                  PDF
                </CustomButton>

                <CustomButton
                  variant={"primary"}
                  type="button"
                  className="btn-theme bg-white text-black text-nowrap max-h-36px px-0"
                  onClick={() => handleExportTimesheetGeneric("excel")}
                >
                  <Download />
                  CSVs
                </CustomButton>
              </div>
            </div>
            <div className="bg-black w-100 h-5px"></div>
          </div>
          <div className="d-block d-lg-none text-center">
            <CustomButton
              variant={"primary"}
              type="button"
              className="btn-theme bg-white text-black text-nowrap max-h-36px"
              onClick={handleOpenExportTimesheet}
            >
              <Download />
              Export PDF Report
            </CustomButton>
          </div>
          <SearchAndFilter
            variant={isClient ? "client-history" : "admin-history"}
            dateRange={dateRange}
            keyword={searchInput}
            selectedCentreIds={selectedCentreIds}
            selectedCandidateIds={selectedCandidateIds}
            onDateRangeChange={setDateRange}
            onKeywordChange={setSearchInput}
            onCentreIdsChange={(ids) => handleCentreSelect(ids)}
            onCandidateIdsChange={setSelectedCandidateIds}
            handleSortChange={handleSortChange}
          />
          <div className="d-flex flex-column">
            <TableHeader
              variant={isClient ? "client-history" : "admin-history"}
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSortChange={handleSortChange}
            />
            {isLoading ? (
              <div className="d-flex gap-20 align-items-center justify-content-center text-center p-15px">
                <div className="fs-16 fw-400 text-grey-medium">
                  Loading timesheets...
                </div>
              </div>
            ) : historyTimesheets.length > 0 ? (
              historyTimesheets.map((timesheet, index) => (
                <div key={index}>
                  <TableRow
                    key={timesheet.id}
                    variant={isClient ? "client-history" : "admin-history"}
                    btnType={"View"}
                    timesheet={timesheet}
                    onViewClick={() => handleViewTimesheet(timesheet)}
                  />
                </div>
              ))
            ) : (
              <div className="d-flex gap-20 align-items-center justify-content-center text-center p-15px">
                <div className="fs-16 fw-400 text-grey-medium">
                  No history timesheets found
                </div>
              </div>
            )}

            {/* Render pagination controls if there are more than 10 users */}
            {historyPagination &&
              historyPagination?.total > DEFAULT_ITEMS_PER_PAGE && (
                <PaginationControls
                  currentPage={page} // Current page number
                  totalPages={historyPagination?.last_page} // Total number of pages
                  paginationLinks={historyPagination?.links} // List of pagination links
                  onPageChange={handlePageChange} // Function to handle page change
                  setPerPageList={setPerPage} // Function to set the number of items per page
                  setCurrentPage={setPage} // Function to set the current page
                />
              )}
          </div>
        </div>
      </div>
      {/* View Timesheet Modal */}
      <PopupModal
        visible={showViewTimesheetModal}
        showCloseBtn={true}
        onClickClose={onClickClose}
        customWidth={650}
        popUpClass="mobile-model"
      >
        {selectedTimesheet && <ViewTimesheet timesheet={selectedTimesheet} timesheetType="sent-to-payroll"/>}
      </PopupModal>
    </>
  );
};

export default AdminTimesheetHistory;
